import { ConnectEntity } from 'components/ConnectEntity';
import { MainComponent } from 'components/MainComponent';
import { VatOverviewPage } from 'pages/vat';
import * as React from 'react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import routes from 'routes';
import { turnDrawerOn } from 'store/app/actions';
import { parseCardFilterConfigFromSearchParams } from 'utils/cardFilterConfig';
import { DrawerType, LicenseType } from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import { fetchSubscriptionTypesRequest } from 'store/billing/actions';
import { getBillingHasAdvancedRights } from 'store/billing/selectors';
import { getCommonCountries } from 'store/common/selectors';

import {
  getOrgLoading,
  getOrgMappedReturns,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import { getTaxRatesGettingTaxes } from 'store/tax-rates/selectors';
import { deleteMappingRequest } from 'store/returns/actions';
import { getReturnLoading } from 'store/returns/selectors';
import { fetchRunDataFailure, generateReportFailure } from 'store/vat/actions';
import { getVatPrepLoading } from 'store/vat/selectors';
import { checkPageLoaded } from 'utils/check-page-loaded';
import { getPwaTaxReturnsPath, isPwaTaxReturnCreationAllowed } from 'utils/pwa-nav';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import {
  Preference,
  PreferenceActions,
  usePreferences,
  PreferenceSections,
} from 'utils/preferences';
import { returnMappingEditPath, reportViewPath } from 'utils/tifd-mapping';

export const PAGE_SIZE_MAIN = 'page-size-main';
export const MAIN_TUTORIAL_HIDE = 'main-tutorial-hide';
export const MAIN_VIEW_MODE = 'main-view-mode';

export const SubmissionsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const mappedReturns = useSelector(getOrgMappedReturns);
  const entities = useSelector(getOrgOrganisations);
  const gettingTaxes = useSelector(getTaxRatesGettingTaxes);
  const countries = useSelector(getCommonCountries);
  const currentUser = useSelector(getAuthUser);
  const orgLoading = useSelector(getOrgLoading);
  const retLoading = useSelector(getReturnLoading);
  const vatLoading = useSelector(getVatPrepLoading);
  const hasAdvancedUserRightsPre = useSelector(getBillingHasAdvancedRights);
  const userLicence = useSelector(getAuthUserLicence);
  const [hasAdvancedUserRights, setHasAdvancedUserRights] =
    useState<boolean>(false);
  React.useEffect(() => {
    setHasAdvancedUserRights(
      hasAdvancedUserRightsPre || userLicence?.licenseType === LicenseType.TRIAL
    );
  }, [hasAdvancedUserRightsPre, userLicence]);

  const [showPage, setShowPage] = React.useState(false);
  const loading = orgLoading || retLoading || vatLoading;
  const preferences = usePreferences();

  const handlePreferences = (
    action: PreferenceActions,
    preference: Preference
  ) => {
    if (currentUser)
      return preferences(
        action,
        PreferenceSections.MAIN,
        preference,
        currentUser?.userUuid
      );
    return false;
  };

  const addReturnMapping = (connectionId: string) => {
    if (connectionId) {
      navigate(routes.returns.add.replace(':connectionId', connectionId));
    } else alert(t('submissions.missing-connection-id'));
  };

  const emptyPageCallback = () => {
    if (!!entities && entities.length > 0) {
      navigate(routes.organisations.all);
    } else {
      const drawerData: DrawerData = {
        title: t('organisation.add-new-entity'),
        subtitle: t('organisation.connect-entity-subtitle'),
        type: DrawerType.CONNECT_ENTITY,
        component: <ConnectEntity />,
      };
      dispatch(turnDrawerOn(drawerData));
    }
  };

  const editReturnMapping = (connectionId: string, mappingId: string) => {
    if (connectionId && mappingId) {
      const returnTypeId = mappedReturns?.find(
        (mappedReturn) => mappedReturn.mappingId === mappingId
      )?.returnTypeId;
      navigate(returnMappingEditPath(connectionId, mappingId, returnTypeId));
    } else alert(t('submissions.missing-connection-id'));
  };

  const deleteReturnMapping = (organisationId: string, mappingId: string) => {
    if (organisationId && mappingId) {
      dispatch(deleteMappingRequest(organisationId, mappingId));
    } else alert(t('submissions.missing-connection-mapping-id'));
  };

  const vatGenerate = (
    connectionId: string,
    mappingId: string,
    dataLogId: string
  ) => {
    if (dataLogId) {
      const returnTypeId = mappedReturns?.find(
        (mappedReturn) => mappedReturn.mappingId === mappingId
      )?.returnTypeId;
      navigate(
        reportViewPath(connectionId, mappingId, dataLogId, returnTypeId)
      );
      return;
    }
    if (!isPwaTaxReturnCreationAllowed()) {
      return;
    }
    openVatWindow(connectionId, mappingId);
  };

  const openVatWindow = (
    connectionId: string,
    mappingId: string
    //dataLogId?: string
  ) => {
    if (!isPwaTaxReturnCreationAllowed()) {
      return;
    }
    const modalData: DrawerData = {
      type: DrawerType.ADD_TAX_SUBMISSION,
      title: t('submissions.create-tax-submission'),
      component: (
        <VatOverviewPage connectionId={connectionId} mappingId={mappingId} />
      ),
    };

    dispatch(turnDrawerOn(modalData));
  };

  const onAddEntity = () => {
    const drawerData: DrawerData = {
      title: t('organisation.connect-entity-title'),
      subtitle: t('organisation.connect-entity-subtitle'),
      type: DrawerType.CONNECT_ENTITY,
      component: <ConnectEntity />,
    };
    dispatch(turnDrawerOn(drawerData));
  };

  // Read query parameters for filters and view
  const queryParams = new URLSearchParams(location.search);
  const cardFilterConfig = parseCardFilterConfigFromSearchParams(queryParams); // New simple filter config
  const filterType = queryParams.get('filter');
  const filterValue = queryParams.get('value');
  const statusFilter = queryParams.get('statusFilter'); // Additional status filter for combined filters
  const personFilter = queryParams.get('personFilter'); // Person filter (format: "role:personName")
  const viewId = queryParams.get('view'); // View ID to load (e.g., from dashboard card)
  const pwaListViewOnly = usePwaMobileNavEnabled();

  React.useEffect(() => {
    setShowPage(true);
    
    // Removed redirect logic - authentication is handled by route guards/Layout
    // The redirect was causing issues during page refresh when currentUser temporarily becomes false
    // during session checks or token refresh operations
    
    // Only proceed with page initialization if we have a user
    // If user is not loaded yet, the effect will re-run when currentUser changes
    if (currentUser) {
      checkPageLoaded(location.pathname, routes.main);
      dispatch(fetchRunDataFailure());
      dispatch(generateReportFailure());
      //dispatch(fetchSubscriptionTypesRequest()); //@-Billing//
    }
  }, [currentUser]);

  // Keep query parameters in URL (removed cleanup logic per user request)

  return (
    <React.Fragment>
      {showPage && (
        <>
          <Helmet>
            <title>{`${t('navigation.returns')} | ${process.env.PROJECT_NAME}`}</title>
            <meta name="description" content={t('submissions.page-description')} />
          </Helmet>
          <MainComponent
            loading={loading}
            mappedReturns={mappedReturns}
            entities={entities}
            countries={countries}
            addReturnMapping={addReturnMapping}
            editReturnMapping={editReturnMapping}
            deleteReturnMapping={deleteReturnMapping}
            vatGenerate={vatGenerate}
            currentUser={currentUser}
            emptyPageCallback={emptyPageCallback}
            hasEntities={!!entities && entities.length > 0}
            gettingTaxes={gettingTaxes}
            connectEntity={onAddEntity}
            handlePreferences={handlePreferences}
            hasAdvancedUserRights={hasAdvancedUserRights}
            initialFilter={
              filterType || statusFilter
                ? {
                    type: filterType || 'status',
                    value: filterValue || statusFilter || undefined,
                    statusFilter: statusFilter || undefined,
                    personFilter: personFilter || undefined,
                  }
                : undefined
            }
            initialViewId={viewId || undefined}
            cardFilterConfig={cardFilterConfig || undefined}
            onClearFiltersNavigation={() =>
              navigate(
                pwaListViewOnly ? getPwaTaxReturnsPath() : routes.submissions
              )
            }
            listViewOnly={pwaListViewOnly}
          />
        </>
      )}
    </React.Fragment>
  );
};
